import { Type } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ICustomCta, ICustomCtaForm } from '@campaign-form/campaign-form.interface';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { AudioTrackingComponent } from './audio-tracking/audio-tracking.component';
import { CaptionComponent } from './caption/caption.component';
import { ChatbotMultiComponent } from './chatbot-multi/chatbot-multi.component';
import { IChatbotMultiDeliverable, IChatbotMultiDeliverableForm } from './chatbot-multi/chatbot-multi.interface';
import { ChatbotComponent } from './chatbot/chatbot.component';
import { IChatbotDeliverable, IChatbotDeliverableForm } from './chatbot/chatbot.interface';
import { ContentComponent } from './content/content.component';
import { CustomComponent } from './custom/custom.component';
import { DescriptionComponent } from './description/description.component';
import { GamePlayComponent } from './game-play/game-play.component';
import { IGamePlayDeliverable, IGamePlayDeliverableForm } from './game-play/game-play.interface';
import { HashtagComponent } from './hashtag/hashtag.component';
import { MentionComponent } from './mention/mention.component';
import { MessageComponent } from './message/message.component';
import { ProductPlacementComponent } from './product-placement/product-placement.component';
import {
  IProductPlacementDeliverable,
  IProductPlacementDeliverableForm,
} from './product-placement/product-placement.interface';
import { IImageDeliverable, IImageDeliverableForm } from './shared/image-deliverable/image-deliverable.interface';
import { ITextDeliverable, ITextDeliverableForm } from './shared/text-deliverable/text-deliverable.interface';
import {
  ITextDescriptionDeliverable,
  ITextDescriptionDeliverableForm,
} from './shared/text-description-deliverable/text-description-deliverable.interface';
import { ShowcaseComponent } from './showcase/showcase.component';
import { TalkingPointAiComponent } from './talking-point-ai/talking-point-ai.component';
import { TalkingPointComponent } from './talking-point/talking-point.component';
import { TextTrackingComponent } from './text-tracking/text-tracking.component';
import { TitleComponent } from './title/title.component';
import { VisualComponent } from './visual/visual.component';
import { IVisualDeliverable, IVisualDeliverableForm } from './visual/visual.interface';

export interface IDeliverableConfig {
  createForm: (item?: TDeliverable, creatorId?: string, isViewMode?: boolean) => FormGroup | null;
  component: Type<
    | AudioTrackingComponent
    | CaptionComponent
    | ChatbotComponent
    | ChatbotMultiComponent
    | ContentComponent
    | CustomComponent
    | DescriptionComponent
    | GamePlayComponent
    | HashtagComponent
    | MentionComponent
    | MessageComponent
    | ProductPlacementComponent
    | ShowcaseComponent
    | TalkingPointAiComponent
    | TalkingPointComponent
    | TextTrackingComponent
    | TitleComponent
    | VisualComponent
  >;
}

export interface IBase {
  id: string;
  name: string;
  sponsoredContentRelevant: boolean;
}

export interface IDeliverableBase extends ICustomCta {
  id: string;
  type: EDeliverableType;
  name: string;
  sponsoredContentRelevant: boolean;
}

export interface IDeliverableItem {
  control: FormGroup<TDeliverableForm>;
  index: number;
}

export interface IDeliverableImage extends ICustomCta {
  id: string;
  imageFile?: File | null;
  imageId: string | null;
}

export interface IBaseDeliverableForm extends ICustomCtaForm {
  id: FormControl<string | null>;
  name: FormControl<string | null>;
  type: FormControl<EDeliverableType>;
  isNew: FormControl<boolean | null>;
  sponsoredContentRelevant: FormControl<boolean>;
}

export interface IDeliverableImageForm extends ICustomCtaForm {
  id: FormControl<string | null>;
  imageFile: FormControl<File | null>;
  imageId: FormControl<string | null>;
}

export enum EProcessingTime {
  nearTime = 'near_time',
  hours = 'hours',
  oneToThreeDays = 'one_to_three_days',
  none = 'none',
}

export interface IDeliverableConfigurationApi {
  id: string;
  processingTime: EProcessingTime;
  deliverable: EDeliverableType;
  platformFeature: EPlatformFeature;
  active: boolean;
  category?: EDeliverableCategory;
  isCheckedManually?: boolean;
}

export interface IDeliverableConfiguration extends IDeliverableConfigurationApi {
  originalTypeKey: string;
  processingTimeKey: string;
  platformFeatureType: EPlatformFeature;
  type: EDeliverableType;
  category: EDeliverableCategory;
  isCheckedManually: boolean;
}
export interface IGroupedMenuDeliverableConfigs {
  category: EDeliverableCategory;
  configs: IDeliverableConfiguration[];
}

export interface ITopLevelGroupedMenuDeliverableConfigs {
  isCheckedManually: boolean;
  categoryGroups: IGroupedMenuDeliverableConfigs[];
}

export enum EDeliverableCategory {
  content = 'content',
  branding = 'branding',
  meta = 'meta',
}

export type TDeliverable =
  | ITextDeliverable
  | IChatbotDeliverable
  | IChatbotMultiDeliverable
  | IProductPlacementDeliverable
  | IGamePlayDeliverable
  | IVisualDeliverable
  | IImageDeliverable
  | ITextDescriptionDeliverable;

export type TDeliverableForm =
  | ITextDeliverableForm
  | IChatbotDeliverableForm
  | IChatbotMultiDeliverableForm
  | IProductPlacementDeliverableForm
  | IGamePlayDeliverableForm
  | IVisualDeliverableForm
  | IImageDeliverableForm
  | ITextDescriptionDeliverableForm;

export type TDeliverableConfigMap = Record<EDeliverableType, IDeliverableConfig>;

export interface IDeliverableTypeConfig {
  component: Type<unknown>;
  hasLabel: boolean;
  isChildOptional?: boolean;
}

export interface IGroupedDeliverables {
  config: IDeliverableTypeConfig;
  deliverables: IDeliverableItem[];
  type: EDeliverableType;
}
